import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { EDIT, IDLE, PENDING } from "const"
import { MtDriveFile, MtDriveVersion } from "server/models"
import { deepCopy, hasProjectAccess, StateType } from "client/utils"
import {
    PRM_CC_REVIEW,
    StoreType,
    CcReviewDocumentProps,

    LoadCcReviewDocuments,
    AddCcReviewDocument,
    DeleteCcReviewDocument,
    SetCcReviewHref,

    setCcReviewDocumentLoadState,
    setCcReviewDocumentFileList,
    setCcReviewDocumentActiveFile,
    setCcReviewDocumentActiveVersion,

    createFileName,
    addModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Empty,
    SelectDropdown,
    SelectDropdownItem,
    DeleteDropdownItem,
    Button,
    SpinnerIcon,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,
} from "client/components"

import Drive from "client/pages/drive"

const CcReviewFileList = ({
    fileId,
    versionId,
}: CcReviewDocumentProps) => {
    const baseRoute = useSelector((state: StoreType) => state.ccReviewDocument.baseRoute)
    const activeTab = useSelector((state: StoreType) => state.ccReviewDocument.activeTab)
    const fileList = useSelector((state: StoreType) => state.ccReviewDocument.fileList)
    const activeFile = useSelector((state: StoreType) => state.ccReviewDocument.activeFile)
    const activeVersion = useSelector((state: StoreType) => state.ccReviewDocument.activeVersion)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [addState, setAddState] = useState<StateType>(IDLE)
    const [deleteFile, setDeleteFile] = useState<MtDriveFile>(null)
    const [deleteState, setDeleteState] = useState<StateType>(IDLE)

    useEffect(() => {
        dispatch(setCcReviewDocumentLoadState(PENDING))
        LoadCcReviewDocuments(project.id)
        .then((documents) => {
            let file: MtDriveFile
            let version: MtDriveVersion

            if (fileId) {
                file = documents.find((row) => row.id === fileId)

                if (versionId) {
                    version = file ? file.versions.find((row) => row.id === versionId) : null
                } else {
                    version = file ? file.versions[0] : null
                }
            } else {
                file = documents[0]
                version = file ? file.versions[0] : null
            }

            dispatch(setCcReviewDocumentFileList(documents))
            dispatch(setCcReviewDocumentActiveFile(file))
            dispatch(setCcReviewDocumentActiveVersion(version))
            dispatch(setCcReviewDocumentLoadState(IDLE))
        })
    }, [])

    const openDriveModal = () => {
        dispatch(addModal({
            key: "drive",
            content: <Drive onSelectFile={(newFile) => addFile(newFile)} />,
        }))
    }

    const addFile = (file: MtDriveFile) => {
        setAddState(PENDING)
        AddCcReviewDocument({
            fileId: file.id,
            projectId: project.id,
        }).then(() => {
            LoadCcReviewDocuments(project.id)
            .then((documents) => {
                dispatch(setCcReviewDocumentFileList(documents))
                setAddState(IDLE)
            })
        })
    }

    const closeConfirm = () => {
        setDeleteFile(null)
    }

    const onConfirmDelete = () => {
        setDeleteState(PENDING)
        DeleteCcReviewDocument({
            fileId: deleteFile.id,
            projectId: project.id,
        }).then(() => {
            // review history, state and items will be removed automatically
            const fileListCopy: MtDriveFile[] = fileList ? deepCopy(fileList) : []
            const newFileList = fileListCopy.filter((file) => file.id !== deleteFile.id)
            dispatch(setCcReviewDocumentFileList(newFileList))

            if (activeFile === deleteFile) {
                const document = newFileList.length ? newFileList[0] : null
                const versions = document ? document.versions : []
                const version = versions.length ? versions[0] : null

                dispatch(setCcReviewDocumentActiveFile(document))
                dispatch(setCcReviewDocumentActiveVersion(version))
                SetCcReviewHref(baseRoute, activeTab, document && document.id, version && version.id)
            }

            setDeleteFile(null)
            setDeleteState(IDLE)
        })
    }

    const editAccess = hasProjectAccess(PRM_CC_REVIEW, EDIT)

    const fileStyle: React.CSSProperties = {
        width: "40%",
        marginRight: "15px",
        flexGrow: 1,
    }
    const versionStyle: React.CSSProperties = {
        width: "40%",
    }
    const addButtonStyle: React.CSSProperties = {
        marginLeft: "15px",
        transform: "translateY(18px)",
    }

    return (
        <PageForm row noPaddingBottom>
            <FormGroup style={fileStyle}>
                <Label>{lang.document}</Label>
                <SelectDropdown title={activeFile ? createFileName(activeFile) : <Empty />}>
                    {
                        fileList.map((file) => (
                            <SelectDropdownItem
                                key={file.id}
                                onClick={(e: React.MouseEvent) => {
                                    if (e.target === e.currentTarget) { // click outside "delete" button
                                        const version = file.versions.length ? file.versions[0] : null
                                        dispatch(setCcReviewDocumentActiveFile(file))
                                        dispatch(setCcReviewDocumentActiveVersion(version))
                                        SetCcReviewHref(baseRoute, activeTab, file.id, version && version.id)
                                    }
                                }}
                            >
                                {createFileName(file)}
                                {
                                    editAccess
                                    && <DeleteDropdownItem onClick={() => setDeleteFile(file)} />
                                }
                            </SelectDropdownItem>
                        ))
                    }
                </SelectDropdown>
            </FormGroup>
            <FormGroup style={versionStyle}>
                <Label>{lang.revision}</Label>
                <SelectDropdown title={activeVersion ? activeVersion.revision : <Empty />}>
                    {
                        activeFile && activeFile.versions.map((version) => (
                            <SelectDropdownItem
                                key={version.id}
                                onClick={() => {
                                    dispatch(setCcReviewDocumentActiveVersion(version))
                                    SetCcReviewHref(baseRoute, activeTab, activeFile.id, version.id)
                                }}
                            >
                                {version.revision}
                            </SelectDropdownItem>
                        ))
                    }
                </SelectDropdown>
            </FormGroup>
            {
                editAccess && (
                    <Button
                        variant="success"
                        style={addButtonStyle}
                        onClick={openDriveModal}
                    >
                        {addState === PENDING ? <SpinnerIcon /> : lang.add}
                    </Button>
                )
            }

            <ConfirmWrapper
                show={!!deleteFile}
                onClose={closeConfirm}
            >
                <Confirm>
                    <ConfirmTitle>
                        {deleteFile && createFileName(deleteFile)}
                    </ConfirmTitle>
                    <ConfirmDescription>{lang.confirmDeleteReviewDocument}</ConfirmDescription>

                    <ConfirmBottom>
                        <Button row onClick={closeConfirm}>{lang.cancel}</Button>
                        <Button variant="success" row onClick={onConfirmDelete}>
                            {deleteState === PENDING ? <SpinnerIcon /> : lang.confirm}
                        </Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>
        </PageForm>
    )
}
export default CcReviewFileList
